import MapboxDraw from '@mapbox/mapbox-gl-draw'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { WithStyles, withStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import React, { useContext, useEffect, useState } from 'react'

import { MapContext } from './index'

interface IDrawLayer extends WithStyles {
  active: boolean
  children?: JSX.Element | JSX.Element[] | (JSX.Element | undefined)[]
  mode: 'draw_polygon' | 'draw_line_string'
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      zIndex: theme.zIndex.drawer,
      margin: '5px',
      maxWidth: '300px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#FFF',
      padding: theme.spacing(2),
      paddingTop: '25px',
    },
    helpEditText: {
      marginBottom: theme.spacing(2),
    },
    helpEditContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    quitIco: {
      position: 'absolute',
      right: '0px',
      top: '0px',
      margin: '5px',
      cursor: 'pointer',
    },
  })

interface IDrawContext<G extends GeoJSON.Geometry | null = GeoJSON.Geometry> {
  actions: {
    deleteAllFeatures: () => void
  }
  draw: GeoJSON.FeatureCollection<G> | undefined
}

export const DrawContext = React.createContext<IDrawContext>({
  draw: undefined,
  actions: {
    deleteAllFeatures: () => {
      return
    },
  },
})

export const useDraw = function <G extends GeoJSON.Geometry | null = GeoJSON.Geometry>() {
  return useContext<IDrawContext<G>>(DrawContext as any)
}

const drawControl = new MapboxDraw({
  displayControlsDefault: false,
  userProperties: true,
  styles: [
    // polygon fill
    {
      id: 'gl-draw-polygon-fill',
      type: 'fill',
      filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      paint: {
        'fill-color': '#2368A2',
        'fill-outline-color': '#2368A2',
        'fill-opacity': 0.1,
      },
    },
    {
      id: 'gl-draw-polygon-fill-active',
      type: 'fill',
      filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      paint: {
        'fill-color': '#000000',
        'fill-outline-color': '#2368A2',
        'fill-opacity': 0.5,
      },
    },
    // First line of the polygon
    {
      id: 'gl-draw-line-stroke-active',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#2368A2',
        'line-dasharray': [0.2, 2],
        'line-width': 2,
      },
    },
    // polygon outline stroke
    // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
    {
      id: 'gl-draw-polygon-stroke-active',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#2368A2',
        'line-dasharray': [0.2, 2],
        'line-width': 2,
      },
    },
    // polygon fill
    {
      id: 'gl-draw-polygon-fill-static',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
      paint: {
        'fill-color': '#000',
        'fill-outline-color': '#000',
        'fill-opacity': 0.1,
      },
    },
    // polygon outline
    {
      id: 'gl-draw-polygon-stroke-static',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 3,
      },
    },
    // vertex point halos
    {
      id: 'gl-draw-polygon-and-line-vertex-halo-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
      paint: {
        'circle-radius': 5,
        'circle-color': '#FFF',
      },
    },
    // vertex points
    {
      id: 'gl-draw-polygon-and-line-vertex-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
      paint: {
        'circle-radius': 3,
        'circle-color': '#2368A2',
      },
    },

    // INACTIVE (static, already drawn)
    // line stroke
    {
      id: 'gl-draw-line-static',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 3,
      },
    },
  ],
})

const DrawLayer = (props: IDrawLayer) => {
  const { active, children, mode } = props
  const [currentFeatures, setCurrentFeatures] = useState<GeoJSON.FeatureCollection | undefined>(undefined)
  const [displayedControls, setDisplayedControls] = useState(false)
  const [internalMode, setInternalMode] = useState<string | undefined>(undefined)

  const map = useContext(MapContext)

  const updateArea = () => {
    const drawData = drawControl.getAll()
    setCurrentFeatures(drawData)
  }

  const deleteArea = () => {
    setCurrentFeatures(undefined)
  }

  const toggleControls = (showControl: boolean) => {
    if (map) {
      if (showControl) {
        map.addControl(drawControl, 'top-left')
        map.on('draw.create', updateArea)
        map.on('draw.delete', deleteArea)
        map.on('draw.update', updateArea)
        setDisplayedControls(true)
      } else {
        map.removeControl(drawControl)
        map.off('draw.create', updateArea)
        map.off('draw.delete', deleteArea)
        map.off('draw.update', updateArea)
        deleteArea()
        setDisplayedControls(false)
      }
    }
  }

  useEffect(() => {
    if (displayedControls && drawControl && map) {
      if (!currentFeatures) {
        drawControl.changeMode(mode)
      } else {
        drawControl.changeMode('direct_select', {
          featureId: currentFeatures.features[0].id,
        })
      }
    }
  }, [displayedControls, currentFeatures])

  useEffect(() => {
    toggleControls(active)
  }, [active])

  useEffect(() => {
    return () => {
      deleteArea()
    }
  }, [])

  const deleteAllFeatures = () => {
    if (drawControl) {
      drawControl.deleteAll()
      setCurrentFeatures(undefined)
    }
  }

  useEffect(() => {
    setInternalMode(mode)
    if (active) {
      deleteAllFeatures()
      drawControl.changeMode(mode)
    }
  }, [mode])

  if (active) {
    return (
      <DrawContext.Provider
        value={{ draw: internalMode === mode ? currentFeatures : undefined, actions: { deleteAllFeatures } }}
      >
        {children}
      </DrawContext.Provider>
    )
  }
  return null
}

export default withStyles(styles)(DrawLayer)
